import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { alertMeta, SEVERITY, fmtTime } from "@/lib/fleet";
import { ALERT_GROUPS } from "@/lib/taxonomy";
import ConfigPanel from "@/components/ConfigPanel";
import CompositeAlertsPanel from "@/components/CompositeAlertsPanel";
import AlertSettingsModal from "@/components/AlertSettingsModal";
import AlertEpisodesView from "@/pages/AlertEpisodesView";
import AlertTriageView from "@/pages/AlertTriageView";
import NotificationRulesView from "@/pages/NotificationRulesView";
import AlertRulesView from "@/pages/AlertRulesView";
import { useAuth } from "@/context/AuthContext";
import { CheckCheck, Check, Bell, Cpu, UserRound, Trash2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const dl = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href); };

const ALL_ITEM = { key: "all", label: "Toutes les alertes", types: null };
const ALL_ITEMS = ALERT_GROUPS.flatMap((g) => g.items);
const A_COLS = [["type", "Type"], ["vehicle", "Véhicule"], ["driver", "Conducteur"], ["message", "Message (POI / Zone)"], ["severity", "Gravité"], ["date", "Date"]];
const baseCols = () => A_COLS.map((c) => ({ key: c[0], label: c[1], enabled: true, agg: "none", numeric: false }));
const defaultCrit = () => ({ nom: "", units: [], severite: "all", format: "WEB" });

const CRIT_FIELDS = [
  { key: "nom", label: "Nom", type: "text" },
  { key: "units", label: "Unité/Conducteur", type: "units" },
  { key: "severite", label: "Gravité", type: "select", options: [["all", "Toutes"], ["high", "Critique"], ["medium", "Moyen"], ["low", "Info"]] },
  { key: "format", label: "Format", type: "select", options: [["WEB", "WEB (écran)"], ["HTML", "HTML"], ["CSV", "CSV"], ["PDF", "PDF"], ["EXCEL", "Excel"]] },
];

export function AlertsEventsView() {
  const [alerts, setAlerts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [sel, setSel] = useState(() => {
    const cat = new URLSearchParams(window.location.search).get("cat");
    return (cat && ALL_ITEMS.find((i) => i.key === cat)) || ALL_ITEM;
  });
  const [showAcked, setShowAcked] = useState(false);
  const [values, setValues] = useState(defaultCrit());
  const [columns, setColumns] = useState(baseCols());
  const [applied, setApplied] = useState(defaultCrit());
  const [units, setUnits] = useState([]);
  const [saved, setSaved] = useState([]);
  const [fVeh, setFVeh] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const { user } = useAuth();
  const canManage = user && ["admin", "gestionnaire"].includes(user.role);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ limit: "1000" });
    if (!showAcked) p.set("acknowledged", "false");
    if (fVeh) p.set("vehicle_id", fVeh);
    if (fFrom) p.set("date_from", new Date(fFrom + "T00:00:00").toISOString());
    if (fTo) p.set("date_to", new Date(fTo + "T23:59:59").toISOString());
    const r = await api.get(`/alerts?${p.toString()}`);
    setAlerts(r.data);
  }, [showAcked, fVeh, fFrom, fTo]);

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setVehicles(r.data);
      setUnits(r.data.map((x) => ({ value: x.plate, label: `${x.name} · ${x.plate}` })));
    }).catch(() => {});
    loadSaved();
  }, []);
  const loadSaved = () => api.get("/saved-defs?kind=alert").then((r) => setSaved(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const driverByPlate = useMemo(() => { const m = {}; vehicles.forEach((v) => { if (v.plate) m[v.plate] = v.driver; }); return m; }, [vehicles]);
  const countFor = useCallback((types) => (!types ? alerts.length : alerts.filter((a) => types.includes(a.type)).length), [alerts]);

  const ack = async (id) => { await api.post(`/alerts/${id}/ack`); toast.success("Alerte acquittée"); load(); };
  const ackAll = async () => { await api.post("/alerts/ack-all"); toast.success("Toutes les alertes acquittées"); load(); };

  const cellVal = (a, key) => ({ type: alertMeta(a.type).label, vehicle: `${a.vehicle} · ${a.plate}`, driver: driverByPlate[a.plate] || "", message: a.message, severity: (SEVERITY[a.severity] || SEVERITY.low).label, date: fmtTime(a.timestamp) }[key]);

  const runExport = (fmt) => {
    const cols = columns.filter((c) => c.enabled);
    const uv = values.units || [];
    const data = alerts.filter((a) => (!sel.types || sel.types.includes(a.type)) && (values.severite === "all" || a.severity === values.severite) && (!uv.length || uv.includes(a.plate)));
    const header = cols.map((c) => c.label);
    const body = data.map((a) => cols.map((c) => cellVal(a, c.key)));
    const titre = values.nom || sel.label; const fname = `alertes_${sel.key}`;
    if (fmt === "CSV") { dl(new Blob(["\ufeff" + [header.join(";"), ...body.map((r) => r.join(";"))].join("\n")], { type: "text/csv;charset=utf-8;" }), `${fname}.csv`); toast.success("Export CSV généré"); }
    else if (fmt === "HTML") { const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>${titre}</title><style>body{font-family:Arial,sans-serif;padding:24px;color:#1e293b}h1{color:#F5821F;margin:0}h2{color:#334155}table{border-collapse:collapse;width:100%;margin-top:12px}th,td{border:1px solid #cbd5e1;padding:8px;font-size:13px;text-align:left}th{background:#F5821F;color:#fff}tr:nth-child(even){background:#f1f5f9}</style></head><body><h1>Boomerang — Alertes</h1><h2>${titre}</h2><table><thead><tr>${header.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${body.map((r) => `<tr>${r.map((c) => `<td>${c ?? ""}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`; dl(new Blob([html], { type: "text/html;charset=utf-8;" }), `${fname}.html`); toast.success("Export HTML généré"); }
    else if (fmt === "EXCEL") { const ws = XLSX.utils.aoa_to_sheet([header, ...body]); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Alertes"); XLSX.writeFile(wb, `${fname}.xlsx`); toast.success("Export Excel généré"); }
    else { const doc = new jsPDF(); doc.setFontSize(16); doc.setTextColor("#F5821F"); doc.text("Boomerang — Alertes", 14, 16); doc.setFontSize(11); doc.setTextColor("#333"); doc.text(titre, 14, 24); autoTable(doc, { startY: 30, head: [header], body, headStyles: { fillColor: [245, 130, 31] }, styles: { fontSize: 8 } }); doc.save(`${fname}.pdf`); toast.success("Export PDF généré"); }
  };

  const onOK = () => { setApplied(values); if (["CSV", "PDF", "HTML", "EXCEL"].includes(values.format)) setTimeout(() => runExport(values.format), 200); else toast.success("Filtres appliqués"); };
  const onAdd = async () => {
    const name = window.prompt("Nom de la nouvelle alerte :", values.nom || sel.label);
    if (!name) return;
    try { await api.post("/saved-defs", { kind: "alert", name, base_key: sel.key, criteria: values, columns }); toast.success("Alerte enregistrée"); loadSaved(); }
    catch { toast.error("Erreur"); }
  };
  const delSaved = async (id) => { await api.delete(`/saved-defs/${id}`); loadSaved(); };

  const enabledCols = columns.filter((c) => c.enabled);
  const isHw = sel.types && sel.types.length === 0;
  const unitVals = applied.units || [];
  const filtered = alerts.filter((a) => {
    if (sel.types && !sel.types.includes(a.type)) return false;
    if (applied.severite !== "all" && a.severity !== applied.severite) return false;
    if (unitVals.length && !unitVals.includes(a.plate)) return false;
    return true;
  });
  const showCol = (k) => enabledCols.some((c) => c.key === k);

  return (
    <>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <p className="label-mono">Supervision</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-3"><Bell className="w-7 h-7 brand-orange" /> Alertes</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={fVeh} onChange={(e) => setFVeh(e.target.value)} data-testid="alert-filter-vehicle" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs outline-none focus:border-[#17A2B8]">
            <option value="">Tous les véhicules</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}
          </select>
          <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="alert-filter-from" title="Du" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs outline-none focus:border-[#17A2B8]" />
          <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="alert-filter-to" title="Au" className="bg-secondary/60 border border-border rounded-md px-2 py-2 text-xs outline-none focus:border-[#17A2B8]" />
          {(fVeh || fFrom || fTo) && <button onClick={() => { setFVeh(""); setFFrom(""); setFTo(""); }} data-testid="alert-filter-reset" className="text-xs text-muted-foreground hover:text-foreground px-1">Réinitialiser</button>}
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer"><input type="checkbox" checked={showAcked} onChange={(e) => setShowAcked(e.target.checked)} data-testid="show-acked" /> Inclure acquittées</label>
          {canManage && (
            <button onClick={() => setManageOpen(true)} data-testid="manage-alerts-btn" className="flex items-center gap-2 border border-border hover:border-[#17A2B8] hover:text-[#17A2B8] text-muted-foreground font-semibold px-4 py-2.5 rounded-md text-sm transition-colors"><SlidersHorizontal className="w-4 h-4" /> Gérer les alertes</button>
          )}
          <button onClick={ackAll} data-testid="ack-all-btn" className="flex items-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-4 py-2.5 rounded-md text-sm transition-colors"><CheckCheck className="w-4 h-4" /> Tout acquitter</button>
        </div>
      </div>

      <AlertSettingsModal open={manageOpen} onClose={() => setManageOpen(false)} />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <aside className="border border-border rounded-lg bg-card overflow-hidden self-start max-h-[80vh] overflow-y-auto" data-testid="alert-taxonomy">
          <button onClick={() => setSel(ALL_ITEM)} data-testid="alert-cat-all" className={`w-full text-left px-4 py-3 text-sm font-bold flex items-center justify-between ${sel.key === "all" ? "bg-[#17A2B8]/15 text-[#2ec5db]" : "hover:bg-black/5"}`}>
            <span className="flex items-center gap-2"><Bell className="w-4 h-4" /> {ALL_ITEM.label}</span><span className="data text-xs">{alerts.length}</span>
          </button>
          {saved.length > 0 && (
            <div className="border-t border-border">
              <p className="label-mono px-4 pt-3 pb-1">Mes alertes</p>
              {saved.map((s) => (
                <div key={s.id} className="flex items-center group px-2">
                  <button onClick={() => { const base = ALL_ITEMS.find((i) => i.key === s.base_key) || ALL_ITEM; setSel(base); setValues(s.criteria || defaultCrit()); setApplied(s.criteria || defaultCrit()); if (s.columns?.length) setColumns(s.columns); }}
                    data-testid={`saved-alert-${s.id}`} className="flex-1 text-left px-2 py-2 text-[13px] text-[#2ec5db] hover:underline truncate">{s.name}</button>
                  <button onClick={() => delSaved(s.id)} className="opacity-0 group-hover:opacity-100 text-[#EF4444] p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          {ALERT_GROUPS.map((g) => (
            <div key={g.group} className="border-t border-border">
              <p className="label-mono px-4 pt-3 pb-1">{g.group.replace("Alertes ", "")}</p>
              {g.items.map((it) => (
                <button key={it.key} onClick={() => setSel(it)} data-testid={`alert-cat-${it.key}`}
                  className={`w-full text-left px-4 py-2 text-[13px] flex items-center justify-between ${sel.key === it.key ? "bg-[#17A2B8]/15 text-[#2ec5db] font-semibold" : "hover:bg-black/5 text-muted-foreground"}`}>
                  <span className="flex items-center gap-2 truncate">{it.hw && <Cpu className="w-3 h-3 shrink-0 opacity-60" />}<span className="truncate">{it.label}</span></span>
                  {it.hw ? <span className="text-[9px] uppercase tracking-wide opacity-50 shrink-0">matériel</span> : <span className="data text-[11px] shrink-0">{countFor(it.types)}</span>}
                </button>
              ))}
            </div>
          ))}
        </aside>

        <div>
          {sel.key === "composees" ? (
            <CompositeAlertsPanel onChange={load} />
          ) : (
            <ConfigPanel title={values.nom || sel.label} criteriaFields={CRIT_FIELDS} values={values} setValues={setValues}
              columns={columns} setColumns={setColumns} units={units} onOK={onOK} onAdd={onAdd} addLabel="Nouvelle alerte"
              tabs={["Alertes instantanées", "Alertes précédentes", "Alertes planifiées"]} />
          )}

          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <div className="px-4 py-2.5 border-b border-border bg-secondary/40 text-sm font-bold">{values.nom || sel.label}</div>
            {isHw ? (
              <div className="text-center text-muted-foreground py-12 px-6 text-sm flex flex-col items-center gap-2" data-testid="alert-hw-note">
                <Cpu className="w-8 h-8 opacity-40" />Cette alerte nécessite un capteur / matériel spécifique (sonde température, lecteur CAN, débitmètre carburant…). Elle sera active une fois le traceur physique raccordé.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-secondary/40"><tr className="label-mono text-left">{enabledCols.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}<th className="px-4 py-3"></th></tr></thead>
                <tbody className="data">
                  {filtered.map((a) => {
                    const m = alertMeta(a.type); const s = SEVERITY[a.severity] || SEVERITY.low;
                    return (
                      <tr key={a.id} className="border-t border-border hover:bg-black/5" data-testid={`alert-row-${a.id}`}>
                        {showCol("type") && <td className="px-4 py-3"><span className="flex items-center gap-2 font-semibold" style={{ color: m.color }}><m.icon className="w-4 h-4" /> {m.label}</span></td>}
                        {showCol("vehicle") && <td className="px-4 py-3">{a.vehicle} <span className="text-muted-foreground">· {a.plate}</span></td>}
                        {showCol("driver") && <td className="px-4 py-3">{driverByPlate[a.plate] ? <span className="flex items-center gap-1"><UserRound className="w-3.5 h-3.5 brand-teal" /> {driverByPlate[a.plate]}</span> : <span className="text-muted-foreground">—</span>}</td>}
                        {showCol("message") && <td className="px-4 py-3 text-muted-foreground">{a.message}</td>}
                        {showCol("severity") && <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-[11px] font-bold" style={{ color: s.color, background: `${s.color}22`, border: `1px solid ${s.color}55` }}>{s.label}</span></td>}
                        {showCol("date") && <td className="px-4 py-3 text-muted-foreground text-xs">{fmtTime(a.timestamp)}</td>}
                        <td className="px-4 py-3 text-right">{!a.acknowledged ? <button onClick={() => ack(a.id)} data-testid={`ack-${a.id}`} className="text-[#17A2B8] hover:text-foreground p-1.5 rounded hover:bg-[#17A2B8]/20"><Check className="w-4 h-4" /></button> : <span className="text-[10px] text-muted-foreground">acquittée</span>}</td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={enabledCols.length + 1} className="text-center text-muted-foreground py-10">Aucune alerte pour cette catégorie</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function AlertsPage() {
  const params = new URLSearchParams(window.location.search);
  const { user } = useAuth();
  const initial = params.get("tab");
  // Rétrocompat : d'anciens liens pointaient vers events/episodes/notifications -> redirigés vers les nouveaux onglets.
  const legacyMap = { events: "history", episodes: "history", notifications: "rules" };
  const start = legacyMap[initial] || (["triage", "history", "rules", "advanced"].includes(initial) ? initial : "triage");
  const [tab, setTab] = useState(start);
  const showAdvanced = canConfig(user);
  const TABS = [
    ["triage", "À traiter"],
    ["history", "Historique"],
    ["rules", "Mes règles"],
    ...(showAdvanced ? [["advanced", "Avancé"]] : []),
  ];
  // Accès au mode avancé A9.1 (règles multi-conditions) : bascule in-app vers l'onglet dédié (sans rechargement).
  const goAdvanced = () => setTab("advanced");
  return (
    <Layout>
      <div className="mb-4">
        <p className="label-mono">Supervision</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-3"><Bell className="w-7 h-7 brand-orange" /> Alertes</h1>
      </div>
      <div className="flex items-center gap-1 mb-5 border-b border-border overflow-x-auto" data-testid="alerts-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === v ? "border-[#17A2B8] text-[#17A2B8]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === "triage" ? <AlertTriageView onGoHistory={() => setTab("history")} />
        : tab === "history" ? <AlertEpisodesView active defaultStatus="closed" historyMode />
        : tab === "advanced" ? <CompositeAlertsPanel onChange={() => {}} />
        : <AlertRulesView onGoEvents={() => setTab("history")} onAdvanced={goAdvanced} canConfig={canConfig(user)} />}
    </Layout>
  );
}

function canConfig(user) { return user && ["admin", "gestionnaire"].includes(user.role); }
